<template>
  <div
    v-if="props.maxDuration"
    class="filter-duration-content"
  >
    <UiChips
      :options="visibleChipsList"
      :modelValue="activeChip"
      small
      @update:modelValue="setActiveChip"
    />
    <div class="filter-duration-content__input-group">
      <UiInput
        :value="rawValue[0]"
        :placeholder="t('duration.fromDay', { day: 1 })"
        :maxLength="2"
        @inputValue="(value: string | number) => updateRawValue(String(value), 0)"
        @focusout="processRawValue"
      />
      <UiInput
        :value="rawValue[1]"
        :placeholder="t('duration.toDay', { day: maxDuration })"
        :maxLength="2"
        @inputValue="(value: string | number) => updateRawValue(String(value), 1)"
        @focusout="processRawValue"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
  import { UiChips, type ChipOption, type UiChipsProps } from '@experience/core-chips-ui';
  import { UiInput } from '@experience/core-input-ui';
  import { useTourFiltersStore } from '@/entities/filter/store/tour';
  import { DAY_HOURS } from '../../const/config.ts';
  import { locales } from '../../i18n';
  import { useComponentI18n } from '@/shared/composables/useComponentI18n.ts';

  interface Props {
    maxDuration: number;
  }

  const props = defineProps<Props>();

  type ChipsModelValue = UiChipsProps['modelValue'];

  const { t } = useComponentI18n(locales);

  const filterStore = useTourFiltersStore();

  const chipsList = ref<ChipOption[]>([
    { label: t('duration.untilTwoDay'), value: 2 },
    { label: t('duration.toDay', { day: 4 }), value: 4 },
    { label: t('duration.toDay', { day: 7 }), value: 7 },
    { label: '10+', value: 10 },
  ]);

  const activeChip = ref<ChipOption | null>(null);

  // Удаляем лишние чипсы в соответсвии с макимальном количеством дней в туре
  const visibleChipsList = computed(() => {
    return chipsList.value.filter((chip) => {
      return typeof chip.value === 'number' && chip.value <= props.maxDuration;
    });
  });

  const rawValue = ref([
    filterStore.state.duration[0] ? filterStore.state.duration[0] / DAY_HOURS : filterStore.state.duration[0],
    filterStore.state.duration[1] ? filterStore.state.duration[1] / DAY_HOURS : filterStore.state.duration[1],
  ]);

  const setActiveChip = (value: ChipsModelValue) => {
    if (!value) {
      activeChip.value = null;
      updateActiveChip();
      updateStore();

      return;
    }
    const chip = Array.isArray(value) ? (value[0] as ChipOption) : (value as ChipOption);
    activeChip.value = chip;

    if (chip.label === '10+') {
      // "10+" → ставим "От", сбрасываем "До"
      rawValue.value = [Number(chip.value), null];
    } else {
      // обычные чипы → сначала проверим конфликт с "От"
      const firstValue = rawValue.value[0];

      if (firstValue !== null && firstValue > Number(chip.value)) {
        // Конфликт — сбрасываем "От"
        rawValue.value = [null, Number(chip.value)];
      } else {
        // Конфликта нет — оставляем "От", ставим "До"
        rawValue.value = [firstValue, Number(chip.value)];
      }
    }

    updateActiveChip();
    updateStore();
  };

  // Валидируем значения в input, на больше 0 и меньше максимального кол-ва дней
  const MAX_LIMIT = 50;

  const validateInputValue = (value: string) => {
    const normalizeValue = Number(value);

    if (normalizeValue > MAX_LIMIT) {
      return MAX_LIMIT;
    }

    return normalizeValue > 0 ? normalizeValue : null;
  };

  // После изменения input делаем чипс активным (если значение input сответсвует какому-либо чипсу)
  const updateActiveChip = () => {
    const chip = visibleChipsList.value.find((el) => {
      if (el.label === '10+') {
        return rawValue.value[0] === el.value;
      }

      return rawValue.value[1] === el.value;
    });

    if (chip) {
      activeChip.value = chip;
    } else {
      activeChip.value = null;
    }
  };

  const lastChangedIndex = ref<number | null>(null);
  const debounceTimer = ref<number | null>(null);

  const updateRawValue = (value: string, id: number) => {
    rawValue.value[id] = validateInputValue(value);
    lastChangedIndex.value = id;

    // Сбросим предыдущий таймер
    if (debounceTimer.value) {
      clearTimeout(debounceTimer.value);
    }

    // Запустим новый таймер (300 мс)
    debounceTimer.value = window.setTimeout(() => {
      processRawValue();
    }, 600);

    updateActiveChip();
    updateStore();
  };

  function processRawValue() {
    const firstValue = rawValue.value[0];
    const secondValue = rawValue.value[1];

    if (firstValue !== null && secondValue !== null && firstValue > secondValue) {
      // Меняем местами
      rawValue.value = [secondValue, firstValue];
    }

    updateActiveChip();
    updateStore();
  }

  const updateStore = () => {
    const first = rawValue.value[0] ? rawValue.value[0] * DAY_HOURS : null;
    const second = rawValue.value[1] ? rawValue.value[1] * DAY_HOURS : null;
    if (filterStore.state.duration[0] !== first || filterStore.state.duration[1] !== second) {
      filterStore.state.duration = [first, second];
    }
  };

  watch(
    () => filterStore.state.duration,
    ([startDuration, endDuration]) => {
      rawValue.value = [
        startDuration !== null ? startDuration / DAY_HOURS : null,
        endDuration !== null ? endDuration / DAY_HOURS : null,
      ];
      updateActiveChip();
    },
    { immediate: true },
  );
</script>

<style scoped lang="scss">
  @use '@/shared/styles/components/input' as input;

  .filter-duration-content__input-group {
    display: flex;
    margin-top: 16px;

    @include input.joined-inputs;
  }
</style>
